import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GetProvidersResponse, GetEmployersResponse, GetBalancesResponse, BalancesRequest, BalancesByProviderRequest, UpdateSicktimeCapRequest, UpdateSicktimeCapResponse } from '../interfaces/sicktime.interface';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { formatUrl } from '../shared/utils/format-url-util';

@Injectable({
  providedIn: 'root'
})
export class SicktimeService {
  constructor(private http: HttpClient) {}

  /**
   * Get providers (employers) for a participant
   * @param participantId - The participant ID
   * @returns Observable with the list of providers
   */
  getProviders(programCode: string, participantId: string): Observable<GetProvidersResponse> {
    const url = formatUrl(ENDPOINTS.SICKTIME.GET_PROVIDERS, {
          program_code: programCode,
          participantId
        });
    return this.http.get<GetProvidersResponse>(url);
  }

  /**
   * Get employers for a provider
   * @param programCode - The program code
   * @param providerId - The provider ID
   * @returns Observable with the list of employers
   */
  getEmployers(programCode: string, providerId: string): Observable<GetEmployersResponse> {
    const url = formatUrl(ENDPOINTS.SICKTIME.GET_EMPLOYERS, {
      program_code: programCode,
      providerId
    });
    return this.http.get<GetEmployersResponse>(url);
  }

  /**
   * Get sick time balances for a participant
   * @param programCode - The program code
   * @param request - Request containing participantId, fromDate, and toDate
   * @returns Observable with the list of sick time balances
   */
  getBalances(programCode: string, request: BalancesRequest): Observable<GetBalancesResponse> {
    const url = formatUrl(ENDPOINTS.SICKTIME.GET_BALANCES, {
      program_code: programCode
    });
    return this.http.post<GetBalancesResponse>(url, request);
  }

  /**
   * Get sick time balances for a provider
   * @param programCode - The program code
   * @param request - Request containing providerId, fromDate, and toDate (dates can be null)
   * @returns Observable with the list of sick time balances
   */
  getBalancesByProvider(programCode: string, request: BalancesByProviderRequest): Observable<GetBalancesResponse> {
    const url = formatUrl(ENDPOINTS.SICKTIME.GET_BALANCES_BY_PROVIDER, {
      program_code: programCode
    });
    return this.http.post<GetBalancesResponse>(url, request);
  }

  /**
   * Get sick time balances filtered by provider and employer
   * @param programCode - The program code
   * @param request - Request containing providerId, optional employerId, fromDate, and toDate
   * @returns Observable with the list of sick time balances
   */
  getBalancesByProviderAndEmployer(programCode: string, request: BalancesByProviderRequest): Observable<GetBalancesResponse> {
    const url = formatUrl(ENDPOINTS.SICKTIME.GET_BALANCES_BY_PROVIDER_AND_EMPLOYER, {
      program_code: programCode
    });
    return this.http.post<GetBalancesResponse>(url, request);
  }

  /**
   * Get sick time balances filtered by participant and provider
   * @param programCode - The program code
   * @param request - Request containing participantId, optional providerId, fromDate, and toDate
   * @returns Observable with the list of sick time balances
   */
  getBalancesByParticipantAndProvider(programCode: string, request: BalancesRequest): Observable<GetBalancesResponse> {
    const url = formatUrl(ENDPOINTS.SICKTIME.GET_BALANCES_BY_PARTICIPANT_AND_PROVIDER, {
      program_code: programCode
    });
    return this.http.post<GetBalancesResponse>(url, request);
  }

  updateSicktimeCap(request: UpdateSicktimeCapRequest): Observable<UpdateSicktimeCapResponse> {
    return this.http.post<UpdateSicktimeCapResponse>(ENDPOINTS.SICKTIME.UPDATE_SICKTIME_CAP, request);
  }
}
